import { ArrowRight, Server } from '@orgus/ui/icons';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Button, EmptyState, MeterBar, Panel, Pressable, RelativeTime, cn, formatInt, formatPercent, useNow } from '@orgus/ui';
import { useAllSessions, useOrgus } from '../../lib/store.ts';
import { fleetRows } from './fleet.ts';
import type { FleetRow } from './fleet.ts';
import { useNeedsYouState } from '../asks/needsYou.ts';
import { blockedOnly } from '../asks/levels.ts';

/** The panel lists this many machines; the rest are a click away on Machines. */
const SHOWN = 6;

const DOT: Record<FleetRow['liveness'], string> = {
  online: 'bg-status-success-fill',
  stale: 'bg-status-attention-fill',
  offline: 'bg-border-strong',
};
const WORD: Record<FleetRow['liveness'], string> = {
  online: 'Online',
  stale: 'Not reporting',
  offline: 'Offline',
};

function Row({ r, seen, open }: { r: FleetRow; seen: number | null; open: () => void }) {
  const on = r.liveness === 'online';
  return (
    <li>
      <Pressable
        onClick={open}
        className="grid w-full min-w-0 grid-cols-12 items-center gap-3 rounded-control px-3 py-2 text-left transition-colors duration-(--duration-fast) hover:bg-hover"
      >
        <span className="col-span-6 flex min-w-0 flex-col max-sm:col-span-12">
          <span className="flex min-w-0 items-center gap-2">
            <span aria-hidden className={cn('size-2 shrink-0 rounded-pill', DOT[r.liveness])} />
            <span className="truncate text-ui font-medium text-fg" title={`${r.name}, ${WORD[r.liveness].toLowerCase()}`}>
              {r.name}
            </span>
          </span>
          <span className="truncate pl-4 text-caption text-fg-subtle">
            {on || r.working ? `${formatInt(r.working)} working` : WORD[r.liveness]}
            {r.needsYou > 0 && <span className="text-status-attention">, {formatInt(r.needsYou)} {r.needsYou === 1 ? 'needs' : 'need'} you</span>}
          </span>
        </span>
        {/* A machine that does not report has no load to draw: no empty meters, only when it was last seen.
            On a phone the meters take their own line under the name, at full width. */}
        {!on ? (
          <span className="col-span-6 text-right text-caption text-fg-subtle max-sm:col-span-12 max-sm:pl-4 max-sm:text-left">
            {seen ? (
              <>
                Seen <RelativeTime ts={seen} />
              </>
            ) : (
              '-'
            )}
          </span>
        ) : (
          <span className="col-span-6 grid grid-cols-2 gap-3 max-sm:col-span-12 max-sm:pl-4">
            {(
              [
                ['CPU', r.load],
                ['Memory', r.mem],
              ] as const
            ).map(([label, v]) => (
              <span key={label} className="flex min-w-0 flex-col gap-1">
                <span className="flex items-baseline justify-between gap-2 text-caption">
                  <span className="text-fg-subtle">{label}</span>
                  <span className="text-fg-muted num">{formatPercent(v)}</span>
                </span>
                <MeterBar value={v} tone={v > 0.9 ? 'attention' : 'neutral'} label={`${label} on ${r.name}: ${formatPercent(v)}`} />
              </span>
            ))}
          </span>
        )}
      </Pressable>
    </li>
  );
}

/**
 * Every machine's share of the work, the busiest first: its live agents, CPU and memory. With
 * many machines it lists the busiest few and links to the rest.
 */
export function FleetNow({ className }: { className?: string }) {
  const machines = useOrgus((s) => s.machines);
  const sessions = useAllSessions();
  const navigate = useNavigate();
  const now = useNow(10_000);
  // Amber only for what blocks (ADR 0056).
  const levels = useNeedsYouState().bySession;
  const rows = useMemo(() => fleetRows(Object.values(machines), blockedOnly(sessions, levels), now), [machines, sessions, now, levels]);
  const online = rows.filter((r) => r.liveness === 'online').length;

  return (
    <Panel icon={<Server />} title="Machines" sub={rows.length ? `${formatInt(online)} of ${formatInt(rows.length)} online` : undefined} className={className} flush>
      {rows.length === 0 ? (
        <EmptyState icon={<Server />} title="No machine yet" description="Machines that run agents show here with their load." />
      ) : (
        <>
          <ul className="flex flex-col px-1 pb-1" aria-label="Machines, busiest first">
            {rows.slice(0, SHOWN).map((r) => (
              <Row key={r.id} r={r} seen={machines[r.id]?.lastSeen ?? null} open={() => navigate(`/machines?machine=${encodeURIComponent(r.id)}`)} />
            ))}
          </ul>
          {rows.length > SHOWN && (
            <div className="flex justify-center pb-3">
              <Button variant="ghost" size="sm" iconRight={<ArrowRight />} onClick={() => navigate('/machines')}>
                All {formatInt(rows.length)} machines
              </Button>
            </div>
          )}
        </>
      )}
    </Panel>
  );
}
